import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth, isAllowedEmail, signOut } from "./firebase";
import { api } from "./api";

type AuthState = {
  user: User | null;
  loading: boolean;
  domainOk: boolean;
  emailVerified: boolean;
  // Admin status comes from the backend (GET /api/me, computed from the
  // ADMIN_EMAILS env var) — null until the lookup resolves. UI that hides
  // admin-only affordances should treat null as "not admin"; route guards
  // should treat null as "still loading" so admins aren't bounced.
  isAdmin: boolean | null;
  // Whether this user may create private (owner-only) deals — from
  // GET /api/me, computed from the PRIVATE_DEAL_EMAILS env var. False for
  // everyone not on that list, which is what keeps the feature invisible
  // to the rest of the team. Not a security boundary: the backend
  // re-checks on create, and access to an existing private deal is
  // owner-only regardless of this flag.
  canCreatePrivateDeals: boolean;
};

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  domainOk: false,
  emailVerified: false,
  isAdmin: null,
  canCreatePrivateDeals: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, "isAdmin" | "canCreatePrivateDeals">>({
    user: null,
    loading: true,
    domainOk: false,
    emailVerified: false,
  });
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [canCreatePrivateDeals, setCanCreatePrivateDeals] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const domainOk = isAllowedEmail(u?.email);
      if (u && !domainOk) {
        // Boot anyone whose email doesn't match an allowed domain.
        signOut();
      }
      setState({
        user: u,
        loading: false,
        domainOk,
        // Google Workspace users are always verified; email/password users
        // become verified once they click their inbox link.
        emailVerified: u?.emailVerified ?? false,
      });
    });
    return unsub;
  }, []);

  // One /api/me round-trip per login: provisions/refreshes the user record
  // server-side and tells us whether this user is an admin.
  useEffect(() => {
    if (!state.user || !state.domainOk) {
      setIsAdmin(null);
      setCanCreatePrivateDeals(false);
      return;
    }
    let cancelled = false;
    api
      .me()
      .then(({ isAdmin, canCreatePrivateDeals: canPrivate }) => {
        if (cancelled) return;
        setIsAdmin(isAdmin === true);
        setCanCreatePrivateDeals(canPrivate === true);
      })
      .catch(() => {
        if (cancelled) return;
        setIsAdmin(false);
        setCanCreatePrivateDeals(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.user, state.domainOk]);

  return (
    <Ctx.Provider value={{ ...state, isAdmin, canCreatePrivateDeals }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}
